"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { Stack } from "@leabankab/component-library";
import {
	CheckboxConsentField,
	ConfirmationButton,
	ConstantAutocompleteField,
	FormContainer,
	RadioCardField,
	SelectField,
} from "@/components/form";
import { type CountryCode } from "@/config/country-config";
import { useCountryConfig } from "@/config/country-context";
import { getCountryRules } from "@/config/country-rules";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import {
	applyMockFormDefaults,
	getMockMyMoneyCustomerDefaults,
} from "@/dev/console/data/mock-form-values";
import { useConstantSelectOptions } from "@/features/customer-information/hooks/use-constant-options";
import { useCustomerInformationSubmit } from "@/features/customer-information/hooks/use-customer-information-submit";
import {
	type MyMoneyCustomerForm,
	resolveMyMoneyAccountNumberForSubmit,
	useMyMoneyCustomerForm,
} from "@/features/customer-information/hooks/use-my-money-customer-form";
import { useTrustedAccountFields } from "@/features/customer-information/hooks/use-trusted-account-fields";
import { resolveSubmitPurpose } from "@/features/customer-information/utils/loan-purpose";
import {
	SWEDISH_ACCOUNT_DIGITS,
	SWEDISH_CLEARING_DIGITS,
	SWEDISH_FULL_ACCOUNT_DIGITS,
} from "@/features/customer-information/utils/swedish-account-fields";
import { DUE_DATE_OPTIONS } from "@/features/customer-information/utils/trusted-account";
import { TaskType } from "@/types";
import {
	MyMoneyCustomerInformationContext,
	MyMoneyCustomerInformationPayload,
} from "../types";
import { AccountNumberField } from "./fields/account-number-field";
import { DigitsInputField } from "./fields/digits-input-field";
import { NumberOfCarsField } from "./fields/number-of-cars-field";

interface Props {
	taskId: string;
	context: MyMoneyCustomerInformationContext;
}

const mapMyMoneyCustomerFormToPayload = ({
	values,
	isCoapplicant,
	purpose,
	country,
	hasMultipleTrustedAccounts,
}: {
	values: MyMoneyCustomerForm;
	isCoapplicant: boolean;
	purpose: string | undefined;
	country: CountryCode;
	hasMultipleTrustedAccounts: boolean;
}): MyMoneyCustomerInformationPayload => {
	const {
		clearingNumber: _clearingNumber,
		isWholeAccountNumber: _isWholeAccountNumber,
		prefilledAccountNumber: _prefilledAccountNumber,
		loanPurpose: _loanPurpose,
		dueDate,
		numberOfCars,
		...rest
	} = values;

	void _clearingNumber;
	void _isWholeAccountNumber;
	void _prefilledAccountNumber;
	void _loanPurpose;

	const { fixedCitizenship, dueDateChoice } = getCountryRules(country);

	return {
		...rest,
		accountNumber: isCoapplicant
			? ""
			: resolveMyMoneyAccountNumberForSubmit({
					values,
					country,
					hasMultipleTrustedAccounts,
				}),
		...(fixedCitizenship ? { citizenship: [...fixedCitizenship] } : {}),
		...(purpose ? { purpose } : {}),
		...(dueDateChoice && dueDate ? { dueDate: Number(dueDate) } : {}),
		...(numberOfCars !== null ? { numberOfCars } : {}),
	};
};

const MyMoneyCustomerComponent = ({ context, taskId }: Props) => {
	const { flowId } = useParams<{ flowId: string }>();
	const t = useTranslations("pages.MyMoneyCustomerInformationPage");
	const tCustomer = useTranslations("pages.CustomerInformationPage");
	const loanPurposeOptions = useConstantSelectOptions("loan-purpose");
	const { country, rules } = useCountryConfig();
	const { mock } = useRuntimeConfig();
	const { isCoapplicant, accountNumbers } = context;
	const hasSwedishAccount = !isCoapplicant && rules.payoutAccount === "swedish";
	const hasFinnishAccount =
		!isCoapplicant && rules.payoutAccount === "finnish-iban";
	const hasMultipleTrustedAccounts =
		rules.payoutAccount === "swedish" && (accountNumbers?.length ?? 0) > 1;

	const trustedAccountFields = useTrustedAccountFields({
		accountNumbers,
		flowId,
		country,
	});
	const { isWholeAccountNumber } = trustedAccountFields;
	// Mutually exclusive: pick from the trusted accounts, correct the one whole
	// number we were given, or type clearing and account separately.
	const showWholeAccountField =
		!hasMultipleTrustedAccounts && isWholeAccountNumber;
	const showSplitAccountFields =
		!hasMultipleTrustedAccounts && !isWholeAccountNumber;

	const { useForm, showLoanPurposeSelect, askNumberOfCars } =
		useMyMoneyCustomerForm({
			isCoapplicant,
			context,
			trustedAccountNumbers: accountNumbers,
		});
	const methods = useForm;
	const { reset, handleSubmit } = methods;

	const submit =
		useCustomerInformationSubmit<MyMoneyCustomerInformationPayload>({
			taskId,
			processType: context.process,
		});

	useEffect(() => {
		const { accountNumber, clearingNumber } = trustedAccountFields;

		reset(
			applyMockFormDefaults(
				{
					citizenship: rules.fixedCitizenship
						? [...rules.fixedCitizenship]
						: (context.citizenships ?? []),
					accountNumber,
					clearingNumber,
					isWholeAccountNumber,
					prefilledAccountNumber: accountNumber,
					loanPurpose: "",
					dueDate: "",
					numberOfCars: null,
				},
				getMockMyMoneyCustomerDefaults(
					country,
					showLoanPurposeSelect,
					askNumberOfCars
				),
				mock
			)
		);
	}, [
		context,
		trustedAccountFields,
		isWholeAccountNumber,
		reset,
		showLoanPurposeSelect,
		askNumberOfCars,
		rules,
		country,
		mock,
	]);

	const onSubmit = handleSubmit(async (values) => {
		const purpose = resolveSubmitPurpose(context, values.loanPurpose);

		if (!purpose) {
			// FLOW rejects a submit without a purpose. Reachable only for a
			// co-applicant whose context carries none, since main applicants are
			// asked for one; log it so the 400 is not a mystery.
			await sendClientLog({
				level: "warn",
				event: LogEvent.FormPurposeMissing,
				message:
					"Submitting customer-information without a purpose: none in context and the customer was not asked",
				task: TaskType.CustomerInformation,
				flowId,
				context: {
					isCoapplicant,
					process: context.process,
					purpose: context.purpose,
				},
			});
		}

		await submit(
			mapMyMoneyCustomerFormToPayload({
				values,
				isCoapplicant,
				purpose,
				country,
				hasMultipleTrustedAccounts,
			})
		);
	});

	const pepOptions = [
		{ value: true, label: t("isPEP.affirmative") },
		{ value: false, label: t("isPEP.opposing") },
	];

	return (
		<FormContainer
			methods={methods}
			onSubmit={onSubmit}
			getPayloadPreview={(values) =>
				mapMyMoneyCustomerFormToPayload({
					values,
					isCoapplicant,
					purpose: resolveSubmitPurpose(context, values.loanPurpose),
					country,
					hasMultipleTrustedAccounts,
				})
			}
		>
			<Stack gap={2.5}>
				{rules.fixedCitizenship === null && (
					<ConstantAutocompleteField
						name="citizenship"
						label={t("citizenship.label")}
						optionsKey="countries"
					/>
				)}

				{hasSwedishAccount && hasMultipleTrustedAccounts && (
					<AccountNumberField
						name="accountNumber"
						label={t("accountNumber.label")}
						accountNumbers={accountNumbers}
						selectLabel={tCustomer("selectInput.labels.accountNumber")}
					/>
				)}

				{hasSwedishAccount && showWholeAccountField && (
					<AccountNumberField
						name="accountNumber"
						label={t("clearingAndAccountNumber.label")}
						digitsOnly
						maxDigits={SWEDISH_FULL_ACCOUNT_DIGITS.max}
					/>
				)}

				{hasSwedishAccount && showSplitAccountFields && (
					<>
						<DigitsInputField
							name="clearingNumber"
							label={t("clearing.label")}
							maxDigits={SWEDISH_CLEARING_DIGITS.max}
						/>
						<AccountNumberField
							name="accountNumber"
							label={t("accountNumber.label")}
							digitsOnly
							maxDigits={SWEDISH_ACCOUNT_DIGITS.max}
						/>
					</>
				)}

				{hasFinnishAccount && (
					<AccountNumberField
						name="accountNumber"
						label={t("accountNumber.label")}
						readOnly={Boolean(trustedAccountFields.accountNumber)}
					/>
				)}

				{showLoanPurposeSelect && (
					<SelectField
						name="loanPurpose"
						label={tCustomer("loanPurpose.label")}
						options={loanPurposeOptions}
					/>
				)}

				{askNumberOfCars && <NumberOfCarsField />}
			</Stack>

			<RadioCardField
				name="isPEP"
				label={t("isPEP.header")}
				tooltip={t("isPEP.tooltip.text")}
				options={pepOptions}
				className="pt-1"
			/>

			{rules.dueDateChoice && !isCoapplicant && (
				<RadioCardField
					name="dueDate"
					label={tCustomer("dueDate.label")}
					options={DUE_DATE_OPTIONS.map((value) => ({
						value,
						label: value,
					}))}
					columns={3}
					className="pt-1"
				/>
			)}

			<Stack gap={1}>
				{!isCoapplicant && (
					<CheckboxConsentField
						name="autogiroConsent"
						label={t("autogiroConsent")}
					/>
				)}

				<CheckboxConsentField
					name="payoutToMyMoneyConsent"
					label={t("payoutToMyMoneyConsent")}
				/>
			</Stack>

			<ConfirmationButton label={t("continueButton")} type="submit" />
		</FormContainer>
	);
};

export default MyMoneyCustomerComponent;
